import React, { useState } from 'react';
import { colors } from '../../styles/Colors';
import { TextRegular } from '../../styles/GlobalStyledComponents';
import {
  CardCheckbox,
  CardColumn,
  CardInput,
  CheckboxContainer,
} from '../CardInputs/CardsStyledComponents';
import { DetailTextWrapper } from '../DetailModal/DetailModalStyledComponents';
import {
  ButtonSection,
  CallInfo,
  CallInfoText,
  CallInfoWrapper,
  CardCheckboxSection,
  CardInputClickModal,
  CardInputSection,
  DateInput,
  DateInputWrapper,
  DateTimeInputWrapper,
  IconCloseWrapper,
  ModalContainer,
  ModalHeader,
  ModalSection,
  OtherPhoneInput,
  OtherPhoneInputWrapper,
  PhoneListWrapper,
  PhoneText,
  CardTextBold,
  TitleBold,
  InputLabel,
  IconPhone,
  OtherPhoneWrapper,
  HeaderFixed,
  CallMeButtonsWrapper,
} from './ClickToCallModalStyledComponents';
import Button from '../Button/Button';
import { theme } from '../../styles/Theme';
import IconClose from '../../assets/icons/IconClose';
import { useUserResult } from '../../contexts/UserContext';
import {
  ClickToCallRequest,
  OriginEnum,
  ProcessType,
} from '../../interfaces/UserData';
import FinancialService from '../../services/Financial';
import { getLocalDateString, validDateHelper, validTimeHelper } from '../../utils/dateTimeHelper';
import GAnalyticsService from '../../services/GAnalytics/ganalytics.service';
import { useLocation } from 'react-router-dom';
import { GTMCallBackPhone, GTMCallBackWhen } from '../../interfaces/GTM';
import { getFlowStep } from '../../utils/dataLayerHelper';
import { PhonesEnum } from '../../interfaces/Phones';
import { validNumberInput } from '../../utils/formHelper';

enum PhoneCases {
  CLIENT_PHONE = 'CLIENT_PHONE',
  OTHER_PHONE = 'OTHER_PHONE',
}

enum CallCases {
  SOON = 'SOON',
  DATE = 'DATE',
}

interface ClickToCallModalProps {
  close: () => void;
  modalSuccess: (getCustomePhone: string) => void;
}

const ClickToCallModal: React.FC<ClickToCallModalProps> = ({
  close,
  modalSuccess,
}) => {
  const { white, red4, red7 } = colors;
  const { textBold } = theme.fontWeight;
  const { pathname } = useLocation();
  const { userResult } = useUserResult();

  const [selectedPhone, setselectedPhone] = useState<PhoneCases>(
    PhoneCases.CLIENT_PHONE
  );
  const [selectedCall, setSelectedCall] = useState<CallCases>(CallCases.SOON);
  const [newPhone, setNewPhone] = useState<string>();
  const [newDate, setNewDate] = useState<string>();
  const [newTime, setNewTime] = useState<string>();
  const [numberError, setNumberError] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [callInputSelected, setCallInputSelected] = useState(false);

  const todayDate = getLocalDateString(new Date());
  const hoursArray = Array.from({ length: 10 }, (_, i) => i + 9);

  const flowCO = sessionStorage.getItem('flow') === 'co';

  const findError = (
    errorList: { code: string; message: string }[],
    errorCode: string
  ) => {
    return errorList.find((e: any) => e.code === errorCode);
  };

  const handleErrors = (error: any) => {
    const phoneError = findError(
      error?.response?.data?.result?.errors,
      'requestBody.phone'
    );
    const dateTimeError = findError(
      error?.response?.data?.result?.errors,
      'requestBody.date'
    );
    if (phoneError || dateTimeError) {
      phoneError && setNumberError(true);
      dateTimeError && setDateError(true);
    } else {
      close();
    }
  };

  const updateCallIcon = (e: any) => {
    if (e.target.value.length > 0) {
      setCallInputSelected(true);
    } else {
      setCallInputSelected(false);
    }
  };

  const clickToCall = async () => {
    try {
      const clientPhone =
        selectedPhone === PhoneCases.CLIENT_PHONE
          ? userResult?.contact_detail.phones[0]
          : newPhone;
      if (
        selectedPhone === PhoneCases.OTHER_PHONE &&
        !validNumberInput(clientPhone ?? '')
      ) {
        setNumberError(true);
        return;
      }
      if (clientPhone && userResult) {
        const data: ClickToCallRequest = {
          document_id: userResult?.document_id,
          process_type: ProcessType.REC,
          phone: clientPhone,
          origin: OriginEnum.WHATSAPP,
        };
        if (selectedCall === CallCases.DATE) {
          data.date = `${newDate}T${newTime ? newTime : '00:00'}:00Z`;
        }
        await FinancialService.clickToCall(data);
        const pathnameString = pathname;
        GAnalyticsService.sendCallBackEvent(
          selectedPhone === PhoneCases.CLIENT_PHONE
            ? GTMCallBackPhone.registered
            : GTMCallBackPhone.other,
          selectedCall === CallCases.SOON
            ? GTMCallBackWhen.asap
            : GTMCallBackWhen.scheduled,
          getFlowStep(pathnameString)
        );
        modalSuccess(getCustomerObfuscatedPhone());
        close();
      }
    } catch (error: any) {
      handleErrors(error);
    }
  };

  // Se filtra y oculta parte del número de teléfono
  const getCustomerObfuscatedPhone = (): string => {
    let phone = userResult?.contact_detail.phones[0];
    let firstpart = phone?.slice(0, 2);
    let lastpart = phone?.slice(-2);
    if (firstpart && lastpart) {
      phone = `${firstpart}******${lastpart}`;
    }
    return phone || '';
  };

  const isBtnDisabled = (): boolean => {
    let isDisabled;
    const clientPhone =
      selectedPhone === PhoneCases.CLIENT_PHONE
        ? userResult?.contact_detail.phones[0]
        : newPhone;
    const clientDate =
      selectedCall === CallCases.DATE && newDate && validDateHelper(newDate)
        ? newDate
        : '';
    const clientDateTime =
      selectedCall === CallCases.DATE && newTime && validTimeHelper(newTime)
        ? newTime
        : '';
    if (
      clientPhone &&
      ((clientDate && clientDateTime) || selectedCall === CallCases.SOON)
    ) {
      isDisabled = false;
    } else {
      isDisabled = true;
    }
    return isDisabled;
  };

  return (
    <ModalContainer id="click-call-modal">
      <ModalHeader id="click-call-modal-header">
        <HeaderFixed id="click-call-fixed-header">
          <TitleBold fontSize={24}>Te llamamos</TitleBold>
          <IconCloseWrapper onClick={() => close()} id="click-call-close-icon">
            <IconClose />
          </IconCloseWrapper>
        </HeaderFixed>
      </ModalHeader>
      <ModalSection id="click-call-modal-section">
        <DetailTextWrapper id="click-call-detail-text">
          <TextRegular fontSize={16} color={'gray6'}>
            Te vamos a llamar al teléfono {getCustomerObfuscatedPhone()}, si
            quieres que te llamemos a otro selecciónalo o introdúcelo abajo.
          </TextRegular>
        </DetailTextWrapper>
        <PhoneListWrapper id="click-call-phone-list">
          <CardInput
            onClick={() => setselectedPhone(PhoneCases.CLIENT_PHONE)}
            cardPadding="10px"
            cardMargin="10px 0 0 0"
            backgroundColor="#FFFFF"
            selected={selectedPhone === PhoneCases.CLIENT_PHONE}
            borderColor={red7}
            id="click-call-client-phone-radio"
          >
            <CheckboxContainer>
              <CardCheckbox
                type="radio"
                checked={selectedPhone === PhoneCases.CLIENT_PHONE}
                readOnly
              />
            </CheckboxContainer>
            <CardColumn>
              <CardTextBold fontSize={16}>
                Llamarme al {getCustomerObfuscatedPhone()}
              </CardTextBold>
            </CardColumn>
          </CardInput>
          <CardInputClickModal
            onClick={() => setselectedPhone(PhoneCases.OTHER_PHONE)}
            cardPadding="10px"
            cardMargin="10px 0 0 0"
            backgroundColor="#FFFFF"
            selected={selectedPhone === PhoneCases.OTHER_PHONE}
            borderColor={red7}
            id="click-call-other-phone-radio"
          >
            <CardCheckboxSection id="click-call-other-phone-checkbox">
              <CheckboxContainer>
                <CardCheckbox
                  type="radio"
                  checked={selectedPhone === PhoneCases.OTHER_PHONE}
                  readOnly
                />
              </CheckboxContainer>
              <CardColumn>
                <CardTextBold fontSize={16}>Otro teléfono</CardTextBold>
              </CardColumn>
            </CardCheckboxSection>
            <CardInputSection
              $selected={selectedPhone === PhoneCases.OTHER_PHONE}
              id="click-call-other-phone-input-section"
            >
              {selectedPhone === PhoneCases.OTHER_PHONE && (
                <OtherPhoneInputWrapper id="click-call-other-phone-input-wrapper">
                  <TextRegular fontSize={14} fontWeight={500}>
                    ¿A qué número quieres que te llamemos?
                  </TextRegular>
                  <OtherPhoneWrapper id="click-call-other-phone-wrapper">
                    <OtherPhoneInput
                      type="text"
                      numberError={numberError}
                      placeholder="Número de teléfono"
                      maxLength={9}
                      inputMode="numeric"
                      value={newPhone ?? ''}
                      onChange={(e) => {
                        // Solo dígitos y máximo 9 cifras
                        const value = e.target.value
                          .replace(/\D/g, '')
                          .slice(0, 9);
                        // Si hay contenido, el primer dígito debe ser 5, 6, 7 o 9
                        if (value && !/^[5679]/.test(value)) {
                          return;
                        }
                        setNewPhone(value);
                        setNumberError(false);
                        setCallInputSelected(value.length > 0);
                      }}
                      onBlur={(e) => {
                        updateCallIcon(e);
                        setNumberError(!!newPhone && !validNumberInput(newPhone));
                      }}
                      onFocus={() => {
                        setCallInputSelected(true);
                      }}
                      id="click-call-other-phone-input"
                    />
                    <IconPhone select={callInputSelected}></IconPhone>
                  </OtherPhoneWrapper>
                </OtherPhoneInputWrapper>
              )}
            </CardInputSection>
          </CardInputClickModal>
        </PhoneListWrapper>
        <DetailTextWrapper id="click-call-date-text">
          <TextRegular fontSize={18} color={'gray6'}>
            ¿Cuándo prefieres que te llamemos?
          </TextRegular>
        </DetailTextWrapper>
        <PhoneListWrapper id="click-call-date-list">
          <CardInput
            onClick={() => {
              setSelectedCall(CallCases.SOON);
              setNewDate('');
              setNewTime('');
            }}
            cardPadding="10px"
            cardMargin="10px 0 0 0"
            backgroundColor="#FFFFF"
            selected={selectedCall === CallCases.SOON}
            borderColor={red7}
            id="click-call-date-soon-radio"
          >
            <CheckboxContainer>
              <CardCheckbox
                type="radio"
                checked={selectedCall === CallCases.SOON}
                readOnly
              />
            </CheckboxContainer>
            <CardColumn>
              <CardTextBold fontSize={16}>Lo antes posible</CardTextBold>
            </CardColumn>
          </CardInput>
          <CardInputClickModal
            onClick={() => setSelectedCall(CallCases.DATE)}
            cardPadding="10px"
            cardMargin="10px 0 0 0"
            backgroundColor="#FFFFF"
            selected={selectedCall === CallCases.DATE}
            borderColor={red7}
            id="click-call-date-custom-radio"
          >
            <CardCheckboxSection id="click-call-date-time-checkbox-section">
              <CheckboxContainer id="click-call-date-custom-checkbox">
                <CardCheckbox
                  type="radio"
                  checked={selectedCall === CallCases.DATE}
                  readOnly
                />
              </CheckboxContainer>
              <CardColumn id="click-call-date-custom--text">
                <CardTextBold fontSize={16}>
                  Elegir fecha y franja horaria
                </CardTextBold>
              </CardColumn>
            </CardCheckboxSection>
            <CardInputSection
              $selected={selectedCall === CallCases.DATE}
              id="click-call-date-time-input-section"
            >
              {selectedCall === CallCases.DATE && (
                <DateInputWrapper id="click-call-date-input-wrapper">
                  <DateTimeInputWrapper id="click-call-date-time-input-wrapper">
                    <DateInput
                      type="date"
                      min={todayDate}
                      dateError={dateError}
                      onChange={(e) => {
                        setNewDate(e.target.value);
                        setDateError(false);
                      }}
                      className={
                        newDate && !validDateHelper(newDate)
                          ? 'input-error'
                          : 'input-valid'
                      }
                      id="click-call-date-input"
                    ></DateInput>
                    <InputLabel htmlFor="click-call-date-input">
                      <TextRegular fontSize={12} color="gray6">
                        Fecha
                      </TextRegular>
                    </InputLabel>
                  </DateTimeInputWrapper>
                  <DateTimeInputWrapper id="click-call-time-input-wrapper">
                    <DateInput
                      type="time"
                      list="limittimeslist"
                      // step="0.01"
                      dateError={dateError}
                      onChange={(e) => {
                        setNewTime(e.target.value);
                        setDateError(false);
                      }}
                      className={
                        newTime && !validTimeHelper(newTime)
                          ? 'input-error'
                          : 'input-valid'
                      }
                      id="click-call-time-input"
                    ></DateInput>
                    <InputLabel htmlFor="click-call-time-input">
                      <TextRegular fontSize={12} color="gray6" fontWeight={500}>
                        Hora
                      </TextRegular>
                    </InputLabel>
                    <datalist id="limittimeslist">
                      {hoursArray.map((value: number) => {
                        return (
                          <option
                            key={value}
                            value={
                              (value === 9 ? '09' : value.toString()) + ':00'
                            }
                          />
                        );
                      })}
                    </datalist>
                  </DateTimeInputWrapper>
                </DateInputWrapper>
              )}
            </CardInputSection>
          </CardInputClickModal>
        </PhoneListWrapper>

        <CallInfoWrapper id="click-call-info-wrapper">
          <CallInfo id="click-call-info">
            <CallInfoText id="click-call-info-text">
              <TextRegular fontSize={16} color="gray6" fontWeight={500}>
                También puedes llamarnos al{' '}
                <PhoneText
                  href={`tel:+34${
                    flowCO ? PhonesEnum.SUPPORT_PHONE_CO : PhonesEnum.SUPPORT_PHONE
                  }`}
                  id="click-call-phone-btn"
                >
                  {flowCO
                    ? PhonesEnum.SUPPORT_PHONE_CO_FORMATTED
                    : PhonesEnum.SUPPORT_PHONE_FORMATTED}
                </PhoneText>
              </TextRegular>
              <div>
                <TextRegular fontSize={14} color="gray8">
                  ¡Estaremos encantados de atenderte!
                </TextRegular>
              </div>
            </CallInfoText>
          </CallInfo>
        </CallInfoWrapper>
      </ModalSection>
      <ButtonSection id="click-call-btn-section">
        <CallMeButtonsWrapper wrapperPadding="0 0 0 0">
          <Button
            color={white}
            backgroundColor={red4}
            fontWeight={textBold}
            disabled={isBtnDisabled() || numberError || dateError}
            click={() => clickToCall()}
            id="click-call-send-btn"
          >
            <p>Confirma</p>
          </Button>
        </CallMeButtonsWrapper>
      </ButtonSection>
    </ModalContainer>
  );
};

export default ClickToCallModal;
